import React, { useMemo } from "react";
import "./CareersIntro.css";
import CountUp from "react-countup";

const tryParseJsonObject = (str) => {
  const t = String(str || "").trim();
  if (!t.startsWith("{") || !t.endsWith("}")) return null;
  try {
    const o = JSON.parse(t);
    return o && typeof o === "object" && !Array.isArray(o) ? o : null;
  } catch {
    return null;
  }
};

const coalesceIntroPayload = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "string") {
    return coalesceIntroPayload(tryParseJsonObject(raw) || {});
  }
  if (typeof raw !== "object") return null;

  const mergeIntroWrapper = (node, into) => {
    const wrap = node?.intro ?? node?.Intro;
    if (!wrap || typeof wrap !== "object") return into;
    if (wrap.data && typeof wrap.data === "object" && !Array.isArray(wrap.data)) {
      return { ...into, ...wrap.data };
    }
    return { ...into, ...wrap };
  };

  let base = { ...raw };
  base = mergeIntroWrapper(raw, base);
  if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) {
    base = { ...base, ...raw.data };
    base = mergeIntroWrapper(raw.data, base);
  }
  if (typeof raw.value === "string") {
    const parsed = tryParseJsonObject(raw.value);
    if (parsed) base = { ...base, ...parsed };
  } else if (raw.value && typeof raw.value === "object" && !Array.isArray(raw.value)) {
    base = { ...base, ...raw.value };
  }
  return mergeIntroWrapper(base, base);
};

const pickString = (...candidates) => {
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return "";
};

const parseStatEnd = (valueRaw) => {
  if (valueRaw == null) return NaN;
  const n = Number.parseFloat(String(valueRaw).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : NaN;
};

const collectIntroStats = (payload) => {
  if (!payload || typeof payload !== "object") return [];
  const out = [];
  for (let i = 1; i <= 12; i++) {
    const valueRaw = payload[`stat${i}Value`] ?? payload[`stat${i}value`];
    const end = parseStatEnd(valueRaw);
    if (!Number.isFinite(end)) continue;
    const label = pickString(payload[`stat${i}Label`], payload[`stat${i}label`]);
    const suffix = pickString(payload[`stat${i}Suffix`], payload[`stat${i}suffix`]);
    out.push({ end, label, suffix });
  }
  return out;
};

const hasMeaningfulIntroPayload = (p) => {
  if (!p || typeof p !== "object") return false;
  if (
    pickString(
      p.titleLine1,
      p.titleLine2,
      p.title,
      p.heading,
      p.description,
      p.body,
      p.text,
      p.summary
    )
  ) {
    return true;
  }
  return collectIntroStats(p).length > 0;
};

const CareersIntro = ({ content }) => {
  const view = useMemo(() => {
    const payload = coalesceIntroPayload(content);
    if (!hasMeaningfulIntroPayload(payload) || !payload) return null;
    const titleLine1 = pickString(payload.titleLine1);
    const titleLine2 = pickString(payload.titleLine2);
    const titleSingle = pickString(payload.title, payload.heading);
    const description = pickString(payload.description, payload.body, payload.text, payload.summary);
    const stats = collectIntroStats(payload);
    return { titleLine1, titleLine2, titleSingle, description, stats };
  }, [content]);

  if (!view) return null;

  const titleBlock =
    view.titleLine1 || view.titleLine2 ? (
      <h2>
        {view.titleLine1}
        {view.titleLine1 && view.titleLine2 ? " " : null}
        {view.titleLine2 ? <span>{view.titleLine2}</span> : null}
      </h2>
    ) : view.titleSingle ? (
      <h2>{view.titleSingle}</h2>
    ) : null;

  return (
    <section className="careers-intro">
      <div className="careers-intro-container">
        {titleBlock}

        {view.description ? <p className="careers-intro-desc">{view.description}</p> : null}

        {view.stats.length > 0 ? (
          <div className="careers-stats">
            {view.stats.map((stat, idx) => (
              <div className="stat" key={`careers-stat-${idx}`}>
                <h3>
                  <CountUp end={stat.end} duration={2} enableScrollSpy scrollSpyOnce />
                  {stat.suffix}
                </h3>
                {stat.label ? <p>{stat.label}</p> : null}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default CareersIntro;
